import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { Referral } from '@/hooks/useReferrals';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { useSorBySorId } from '@/hooks/useSorLookup';
import { useAuth } from '@/lib/auth';
import { useReferrerIdentity } from '@/components/patient/useReferrerIdentity';
import type { PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { EpikriseClinicalFields } from '@/components/referrals/EpikriseClinicalFields';
import { EpikrisePartiesFields } from '@/components/referrals/EpikrisePartiesFields';
import { EpikriseAttachmentsField, EpikriseOtherDiagnosesField } from '@/components/referrals/EpikriseRadiologyFields';
import { EpikriseIssueSummary, type EpikriseBlocker } from '@/components/referrals/EpikriseIssueSummary';
import { EpikriseSentList } from '@/components/referrals/EpikriseSentList';
import { EpikriseTextFields } from '@/components/referrals/EpikriseTextFields';
import {
  FIELD,
  HEADING,
  SECTION,
  filled,
  pickedParty,
  useEpikriseDrafts,
  useEpikriseOutbox,
  type EpikriseCopyRequest,
  type EpikriseDraftRow,
  type EpikriseOutboxRow,
  type EpikriseResult,
} from '@/components/referrals/epikrisePanelShared';
import { Button } from '@/components/ui/Button';
import { ApiCallError, callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import type { Json } from '@/types/database.generated';
import { readClinicMedicalSpecialityCode } from '@/lib/referral/clinicMedicalSpecialityCode';
import { downloadLatin1Xml } from '@/lib/referral/latin1Xml';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import { readPostalAddress } from '@/lib/referral/xref01Validation';
import {
  EPIKRISE_KINDS,
  emptyEpikriseForm,
  epikriseContact,
  epikriseHasMarkering,
  epikriseHasRadiologyExtras,
  epikriseIssues,
  epikriseOtherDiagnosesForRequest,
  epikriseSectionsForRequest,
  isEpikriseKind,
  readEpikriseForm,
  type EpikriseForm,
  type EpikriseKind,
  type Markering,
} from '@/lib/referral/epikrise';
import {
  epikriseAttachmentIssues,
  epikriseAttachmentsForRequest,
  type EpikriseAttachment,
} from '@/lib/referral/epikriseAttachments';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

// Spor A step 3 T1.3 (DIS01/02/03/05-S): "Dan epikrise" from a received XREF01.
// Communicate (T1.2) builds XDIS01/02/03/05 from the stored referral: forløbsnummer,
// receiver, Referral block (id, received, diagnoses), patient and pårørende are
// carried over; the CPR never passes through the browser. The form adds the
// clinical content, markering, kontaktperson, CC and dates. Nothing is sent.

export function EpikrisePanel({ referral }: { referral: Referral }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const clinicQuery = useClinic(referral.clinic_id);
  const connectionQuery = useClinicCommunicateConnection(referral.clinic_id);
  const senderSor = useSorBySorId(connectionQuery.data?.sor_id ?? null);
  const outbox = useEpikriseOutbox(referral.id);
  const drafts = useEpikriseDrafts(referral.id);

  const [form, setForm] = useState<EpikriseForm | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState<EpikriseOutboxRow | null>(null);
  const [chooseReceiver, setChooseReceiver] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<EpikriseResult[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copyFor, setCopyFor] = useState<EpikriseOutboxRow | null>(null);
  const [copyPick, setCopyPick] = useState<PickedReceiver | null>(null);
  const [attachments, setAttachments] = useState<EpikriseAttachment[]>([]);
  // DIS05-S 4.22: the XDIS05 signer is the logged-in user (users.name + the clinic membership title).
  const signerQuery = useReferrerIdentity(user?.id ?? null, referral.clinic_id);

  const medicalSpecialityCode = readClinicMedicalSpecialityCode(connectionQuery.data?.medical_speciality_code);
  const referralRelative = Boolean(referral.parent_1_name?.trim());
  const radiology = Boolean(form && epikriseHasRadiologyExtras(form.kind));
  const signer = radiology && signerQuery.data ? signerQuery.data : undefined;
  const issues = form ? epikriseIssues(form, referralRelative, signer) : [];
  const attachmentIssues = radiology ? epikriseAttachmentIssues(attachments) : {};
  const receiverIssue = Boolean(form?.receiver && !pickedParty(form.receiver));
  const ccIssue = Boolean(form?.ccReceiver && !pickedParty(form.ccReceiver));
  const blockers: EpikriseBlocker[] = [
    ...issues,
    ...(chooseReceiver && !form?.receiver ? (['receiver_missing'] as const) : []),
    ...(receiverIssue ? (['receiver_incomplete'] as const) : []),
    ...(ccIssue ? (['cc_incomplete'] as const) : []),
    ...(radiology && !signerQuery.data ? (['signer_loading'] as const) : []),
    ...(Object.keys(attachmentIssues).length > 0 ? (['attachments_invalid'] as const) : []),
  ];
  const openDrafts = (drafts.data ?? []).filter((d) => d.status === 'draft');

  const senderGap = !connectionQuery.data?.ean
    ? t('referrals.revisit_sender_missing_ean', 'Klinikken har ingen MedCom-forbindelse med lokationsnummer.')
    : !senderSor.data?.identifier || !senderSor.data.identifierCode
      ? t('referrals.revisit_sender_missing_sor', 'Klinikkens SOR-enhed mangler identifikator.')
      : !medicalSpecialityCode
        ? t('referrals.revisit_sender_missing_speciality', 'Klinikkens speciale (MedicalSpecialityCode) er ikke konfigureret.')
        : null;

  const kindLabel = (kind: EpikriseKind) =>
    ({
      XDIS01: t('referrals.epikrise_kind_xdis01', 'Udskrivningsepikrise (XDIS01)'),
      XDIS02: t('referrals.epikrise_kind_xdis02', 'Ambulant epikrise (XDIS02)'),
      XDIS03: t('referrals.epikrise_kind_xdis03', 'Skadestueepikrise (XDIS03)'),
      XDIS05: t('referrals.epikrise_kind_xdis05', 'Billeddiagnostisk epikrise (XDIS05)'),
    })[kind];

  function update(patch: Partial<EpikriseForm>) {
    setForm((f) => (f ? { ...f, ...patch } : f));
  }

  function open(next: EpikriseForm, nextDraftId: string | null, nextCorrecting: EpikriseOutboxRow | null) {
    setForm(next);
    setDraftId(nextDraftId);
    setCorrecting(nextCorrecting);
    setChooseReceiver(Boolean(next.receiver) || next.kind === 'XDIS05');
    setAttempted(false);
    setError(null);
    setResults([]);
    setAttachments([]);
  }

  function openDraft(draft: EpikriseDraftRow) {
    const corrected = draft.corrects_outbox_id
      ? ((outbox.data ?? []).find((r) => r.id === draft.corrects_outbox_id) ?? null)
      : null;
    open(readEpikriseForm(draft.kind, draft.form), draft.id, corrected);
  }

  /** "Ret epikrise": the same form, prefilled from the kladde it was built from (DIS01-S 4.22/4.23). */
  function onCorrect(row: EpikriseOutboxRow) {
    const sentDraft = (drafts.data ?? []).find((d) => d.sent_outbox_id === row.id);
    const restored = sentDraft ? readEpikriseForm(row.kind, sentDraft.form) : emptyEpikriseForm(row.kind);
    open({ ...restored, copyToCc: false }, null, row);
  }

  function closeForm() {
    setAttachments([]);
    setForm(null);
    setDraftId(null);
    setCorrecting(null);
    setAttempted(false);
    setError(null);
  }

  async function saveDraft(current: EpikriseForm): Promise<string> {
    const formJson = JSON.parse(JSON.stringify(current)) as Json;
    if (draftId) {
      const { error: updateError } = await supabase
        .from('clinic_epikrise_drafts')
        .update({ form: formJson, kind: current.kind, updated_at: new Date().toISOString() })
        .eq('id', draftId);
      if (updateError) throw updateError;
      return draftId;
    }
    const { data, error: insertError } = await supabase
      .from('clinic_epikrise_drafts')
      .insert({
        clinic_id: referral.clinic_id,
        referral_id: referral.id,
        kind: current.kind,
        form: formJson,
        status: 'draft',
        corrects_outbox_id: correcting?.id ?? null,
        created_by: user?.id ?? null,
      })
      .select('id')
      .single();
    if (insertError || !data) throw insertError ?? new Error('no row');
    setDraftId(data.id);
    return data.id;
  }

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['referral-epikriser', referral.id] }),
      queryClient.invalidateQueries({ queryKey: ['referral-epikrise-drafts', referral.id] }),
      referral.patient_id
        ? queryClient.invalidateQueries({ queryKey: ['hospital-referral-outbox', referral.clinic_id, referral.patient_id] })
        : Promise.resolve(),
    ]);
  }

  async function onSaveDraft() {
    if (!form) return;
    setPending(true);
    try {
      await saveDraft(form);
      await queryClient.invalidateQueries({ queryKey: ['referral-epikrise-drafts', referral.id] });
      toast.success(t('referrals.epikrise_draft_saved', 'Kladden er gemt. Du kan åbne den igen og fortsætte.'));
    } catch {
      toast.error(t('referrals.epikrise_draft_failed', 'Kladden kunne ikke gemmes.'));
    } finally {
      setPending(false);
    }
  }

  async function buildCopy(originalOutboxId: string, picked: PickedReceiver): Promise<EpikriseResult> {
    return await callFunction<EpikriseCopyRequest, EpikriseResult>('communicate-ref', {
      action: 'epikrise-copy',
      clinicId: referral.clinic_id,
      originalOutboxId,
      receiver: {
        ean: picked.organisation.ean,
        ...(picked.organisation.name ? { organisationName: picked.organisation.name } : {}),
      },
    });
  }

  function requestBody(current: EpikriseForm) {
    const clinic = clinicQuery.data;
    const sor = senderSor.data;
    const connection = connectionQuery.data;
    if (!clinic || !sor?.identifier || !sor.identifierCode || !connection?.ean || !medicalSpecialityCode) return null;
    const clinicAddress = readPostalAddress(clinic.address);
    const contact = epikriseContact(current, epikriseHasRadiologyExtras(current.kind) ? signer : undefined);
    const otherDiagnoses = epikriseOtherDiagnosesForRequest(current);
    const receiver = chooseReceiver && current.receiver ? pickedParty(current.receiver) : null;
    const cc = current.ccReceiver ? pickedParty(current.ccReceiver) : null;
    const ccBody = cc ? { identifier: cc.identifier, identifierCode: cc.identifierCode, ...filled({
      organisationName: cc.organisationName,
      streetName: cc.streetName,
      postCodeIdentifier: cc.postCodeIdentifier,
      districtName: cc.districtName,
    }) } : null;
    return {
      action: 'epikrise' as const,
      clinicId: referral.clinic_id,
      referralId: referral.id,
      kind: current.kind,
      sender: {
        ean: connection.ean,
        ...filled({
          identifier: sor.identifier,
          identifierCode: sor.identifierCode,
          organisationName: clinic.name,
          streetName: clinicAddress.street,
          postCodeIdentifier: clinicAddress.postCode,
          districtName: clinicAddress.district,
          telephoneSubscriberIdentifier: clinic.phone,
          medicalSpecialityCode,
        }),
      },
      ...(receiver ? { receiver } : {}),
      ...(ccBody ? { ccReceiver: ccBody } : {}),
      ...(contact.title || contact.name
        ? {
            contact: {
              ...(contact.title ? { title: contact.title } : {}),
              ...(contact.name ? { name: contact.name } : {}),
            },
          }
        : {}),
      episodeOfCareStatusCode: current.episodeOfCareStatusCode,
      includeReferralRelative: current.includeReferralRelative,
      ...(current.relatives.length
        ? {
            relatives: current.relatives.map((r) => ({
              relationCode: r.relationCode,
              surname: r.surname.trim(),
              ...(r.givenName.trim() ? { givenName: r.givenName.trim() } : {}),
            })),
          }
        : {}),
      ...(current.start.date ? { start: current.start.time ? current.start : { date: current.start.date } } : {}),
      ...(current.end.date ? { end: current.end.time ? current.end : { date: current.end.date } } : {}),
      ...(epikriseHasMarkering(current.kind) && current.markering !== 'none'
        ? { actionPriority: current.markering, recommendation: current.recommendation.replace(/\s+$/, '') }
        : {}),
      ...(current.mainDiagnosis ? { mainDiagnosis: current.mainDiagnosis } : {}),
      ...(current.mainAdditional.length ? { mainAdditional: current.mainAdditional } : {}),
      ...(otherDiagnoses.length ? { otherDiagnoses } : {}),
      sections: epikriseSectionsForRequest(current),
      ...(correcting ? { correctsOutboxId: correcting.id } : {}),
    };
  }

  async function onSubmit() {
    if (!form) return;
    setAttempted(true);
    setError(null);
    if (senderGap) {
      toast.error(senderGap);
      return;
    }
    if (blockers.length > 0) {
      toast.error(t('referrals.epikrise_invalid', 'Epikrisen er ikke dannet. Ret følgende:'));
      return;
    }
    const body = requestBody(form);
    if (!body) {
      const message = t('referrals.epikrise_not_ready', 'Klinikkens MedCom-oplysninger er ikke indlæst endnu. Prøv igen om lidt.');
      setError(message);
      toast.error(message);
      return;
    }
    setPending(true);
    try {
      const id = await saveDraft(form);
      const files =
        epikriseHasRadiologyExtras(form.kind) && attachments.length > 0 ? await epikriseAttachmentsForRequest(attachments) : [];
      const built = await callFunction<typeof body & { draftId: string; attachments?: typeof files }, EpikriseResult>(
        'communicate-ref',
        { ...body, draftId: id, ...(files.length ? { attachments: files } : {}) },
      );
      const done: EpikriseResult[] = [built];
      let copyError: string | null = null;
      if (form.copyToCc && form.ccReceiver && body.ccReceiver) {
        try {
          done.push(await buildCopy(built.outboxId, form.ccReceiver));
        } catch (err) {
          copyError = err instanceof ApiCallError ? err.message : t('referrals.epikrise_copy_failed', 'Kopien kunne ikke dannes.');
        }
      }
      setResults(done);
      await refresh();
      toast.success(
        correcting
          ? t('referrals.epikrise_corrected', 'Den rettede epikrise er dannet og ligger i udbakken.')
          : t('referrals.epikrise_built', 'Epikrisen er dannet og ligger i udbakken.'),
      );
      if (copyError) toast.error(copyError);
      setForm(null);
      setDraftId(null);
      setCorrecting(null);
      setAttempted(false);
      setAttachments([]);
    } catch (err) {
      const message = err instanceof ApiCallError ? err.message : t('referrals.epikrise_failed', 'Epikrisen kunne ikke dannes.');
      setError(message);
      toast.error(message);
      await queryClient.invalidateQueries({ queryKey: ['referral-epikrise-drafts', referral.id] });
    } finally {
      setPending(false);
    }
  }

  async function onDownload(row: EpikriseOutboxRow) {
    setBusyId(row.id);
    try {
      const payload = await callFunction<{ action: 'payload'; clinicId: string; exchangeId: string }, { xml?: string }>(
        'communicate-ref',
        { action: 'payload', clinicId: referral.clinic_id, exchangeId: row.exchange_id },
      );
      if (payload.xml === undefined) throw new Error('Missing XML payload');
      downloadLatin1Xml(payload.xml, `${row.kind}_${row.envelope_identifier}.xml`);
    } catch (err) {
      toast.error(
        err instanceof ApiCallError ? err.message : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onDownloadCompanion(row: EpikriseOutboxRow, companion: { exchangeId: string; fileName: string }) {
    setBusyId(row.id);
    try {
      const payload = await callFunction<{ action: 'payload'; clinicId: string; exchangeId: string }, { xml?: string }>(
        'communicate-ref',
        { action: 'payload', clinicId: referral.clinic_id, exchangeId: companion.exchangeId },
      );
      if (payload.xml === undefined) throw new Error('Missing XML payload');
      downloadLatin1Xml(payload.xml, `XBIN01_${companion.fileName.replace(/[\\/]/g, '_')}.xml`);
    } catch (err) {
      toast.error(
        err instanceof ApiCallError ? err.message : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onSendCopy() {
    if (!copyFor) return;
    if (!copyPick?.organisation.ean) {
      toast.error(t('referrals.epikrise_copy_receiver_missing', 'Vælg kopimodtageren i SOR.'));
      return;
    }
    setBusyId(copyFor.id);
    try {
      const built = await buildCopy(copyFor.id, copyPick);
      setResults([built]);
      setCopyFor(null);
      setCopyPick(null);
      await refresh();
      toast.success(t('referrals.epikrise_copy_built', 'Kopien er dannet og ligger i udbakken.'));
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('referrals.epikrise_copy_failed', 'Kopien kunne ikke dannes.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className={SECTION} aria-labelledby="referral-epikrise-heading" data-testid="referral-epikrise-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ds-line px-4 py-2.5">
        <span id="referral-epikrise-heading" className={HEADING}>
          {t('referrals.epikrise_title', 'Epikrise')}
        </span>
        {!form && (
          <span className="flex flex-wrap items-center gap-2">
            <label htmlFor="epikrise-new-kind" className="sr-only">
              {t('referrals.epikrise_choose_kind', 'Vælg epikrisetype')}
            </label>
            <select
              id="epikrise-new-kind"
              data-testid="epikrise-new-kind"
              disabled={!referral.patient_id}
              value=""
              onChange={(e) => {
                if (isEpikriseKind(e.target.value)) open(emptyEpikriseForm(e.target.value), null, null);
              }}
              className={cn(
                TYPE.inputValue,
                'rounded-md border border-ds-field px-2 py-1.5 text-sm   ',
              )}
            >
              <option value="">{t('referrals.epikrise_open', 'Dan epikrise …')}</option>
              {EPIKRISE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {kindLabel(kind)}
                </option>
              ))}
            </select>
          </span>
        )}
      </div>

      {!referral.patient_id && (
        <p className={cn('px-4 py-3', FORM_HINT_TYPE)}>
          {t('referrals.epikrise_needs_patient', 'Knyt henvisningen til en patient, før der dannes en epikrise.')}
        </p>
      )}

      {!form && openDrafts.length > 0 && (
        <div className="border-b border-ds-line px-4 py-3" data-testid="epikrise-drafts">
          <div className={HEADING}>{t('referrals.epikrise_drafts', 'Kladder')}</div>
          <ul className="mt-2 divide-y divide-ds-line">
            {openDrafts.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <span className="text-ds-text">
                  {kindLabel(d.kind)}
                  {d.corrects_outbox_id ? ` · ${t('referrals.epikrise_rettet', 'Rettet')}` : ''} ·{' '}
                  {t('referrals.epikrise_draft_saved_at', 'gemt')} {formatReferralDateTime(d.updated_at)}
                </span>
                <Button type="button" size="sm" variant="secondary" onClick={() => openDraft(d)}>
                  {t('referrals.epikrise_draft_open', 'Fortsæt kladde')}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {form && (
        <div className="space-y-4 px-4 py-4" data-testid="epikrise-form">
          {correcting && (
            <div
              role="status"
              data-testid="epikrise-correcting-banner"
              className="rounded-md border border-ds-amber-line bg-ds-amber-tint px-3 py-2 text-sm text-ds-amber-ink"
            >
              <p className="font-medium">
                {t('referrals.epikrise_correcting', 'Du retter en tidligere dannet epikrise (kuvert {{envelope}}, {{date}}).', {
                  envelope: correcting.envelope_identifier,
                  date: formatReferralDateTime(correcting.created_at),
                })}
              </p>
              <p className="mt-0.5">
                {t(
                  'referrals.epikrise_correcting_hint',
                  'Den dannes som rettet epikrise (StatusCode rettetbrev) med nyt kuvert- og brev-id og samme henvisningsblok.',
                )}
              </p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="epikrise-kind" className={cn('block', FORM_LABEL_TYPE)}>
                {t('referrals.epikrise_kind', 'Epikrisetype')}
              </label>
              <select
                id="epikrise-kind"
                value={form.kind}
                disabled={Boolean(correcting)}
                onChange={(e) => {
                  if (!isEpikriseKind(e.target.value)) return;
                  const kind = e.target.value;
                  update({ kind, ...(epikriseHasMarkering(kind) ? {} : { markering: 'none' as Markering, recommendation: '' }) });
                  if (kind === 'XDIS05') setChooseReceiver(true);
                }}
                className={cn(TYPE.inputValue, FIELD)}
              >
                {EPIKRISE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {kindLabel(kind)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="epikrise-status" className={cn('block', FORM_LABEL_TYPE)}>
                {t('referrals.epikrise_patient_status', 'Patientstatus (EpisodeOfCareStatusCode)')}
              </label>
              <select
                id="epikrise-status"
                value={form.episodeOfCareStatusCode}
                onChange={(e) => update({ episodeOfCareStatusCode: e.target.value as EpikriseForm['episodeOfCareStatusCode'] })}
                className={cn(TYPE.inputValue, FIELD)}
              >
                <option value="inaktiv">{t('referrals.epikrise_status_inaktiv', 'Inaktiv (afsluttet)')}</option>
                <option value="indlagt">{t('referrals.epikrise_status_indlagt', 'Indlagt')}</option>
                <option value="ambulant">{t('referrals.epikrise_status_ambulant', 'Ambulant')}</option>
                <option value="ambulant_roentgen">{t('referrals.epikrise_status_roentgen', 'Ambulant røntgen')}</option>
                <option value="doed">{t('referrals.epikrise_status_doed', 'Død')}</option>
              </select>
            </div>
          </div>

          <EpikrisePartiesFields
            referral={referral}
            form={form}
            update={update}
            chooseReceiver={chooseReceiver}
            setChooseReceiver={setChooseReceiver}
            attempted={attempted}
            receiverIssue={receiverIssue}
            ccIssue={ccIssue}
          />
          <EpikriseClinicalFields
            referral={referral}
            form={form}
            update={update}
            attempted={attempted}
            issues={issues}
            signer={{
              name: signerQuery.data?.name ?? null,
              title: signerQuery.data?.title ?? null,
              loading: signerQuery.isLoading,
            }}
          />
          {radiology && <EpikriseOtherDiagnosesField form={form} update={update} attempted={attempted} issues={issues} />}
          <EpikriseTextFields form={form} update={update} attempted={attempted} issues={issues} />
          {radiology && (
            <EpikriseAttachmentsField
              attachments={attachments}
              setAttachments={setAttachments}
              issues={attachmentIssues}
              attempted={attempted}
            />
          )}

          {senderGap && (
            <p role="alert" className={FORM_ERROR_TYPE}>
              {senderGap}
            </p>
          )}
          {attempted && <EpikriseIssueSummary kind={form.kind} blockers={blockers} />}
          {error && (
            <p role="alert" className={FORM_ERROR_TYPE} data-testid="epikrise-error">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void onSubmit()} disabled={pending} data-testid="epikrise-submit">
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {correcting ? t('referrals.epikrise_correct_submit', 'Dan rettet epikrise') : t('referrals.epikrise_submit', 'Dan epikrise')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => void onSaveDraft()} disabled={pending} data-testid="epikrise-save-draft">
              {t('referrals.epikrise_save_draft', 'Gem kladde')}
            </Button>
            <Button type="button" variant="secondary" onClick={closeForm} disabled={pending}>
              {t('common.cancel', 'Annuller')}
            </Button>
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="border-t border-ds-line px-4 py-3 text-sm" data-testid="epikrise-checks">
          {results.map((r) => (
            <div key={r.id} className="mb-2">
              <div className="font-medium text-ds-text">
                {kindLabel(r.kind)} · {r.envelopeIdentifier}
              </div>
              <ul className="space-y-1">
                {r.checks.map((check) => (
                  <li key={check.id} className={check.pass ? 'text-ds-green-ink ' : 'text-ds-red-ink'}>
                    {check.pass ? '✓' : '✗'} {check.detail}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <EpikriseSentList
        rows={outbox.data ?? []}
        kindLabel={kindLabel}
        busyId={busyId}
        correctDisabled={pending || Boolean(senderGap) || Boolean(form)}
        copyFor={copyFor}
        copyPick={copyPick}
        onCorrect={onCorrect}
        onToggleCopy={(row) => {
          setCopyFor(copyFor?.id === row.id ? null : row);
          setCopyPick(null);
        }}
        onCopyPick={setCopyPick}
        onSendCopy={() => void onSendCopy()}
        onDownload={(row) => void onDownload(row)}
        onDownloadCompanion={(row, companion) => void onDownloadCompanion(row, companion)}
      />
    </section>
  );
}
